/**
 * AI Configuration and Environment Setup
 * Enterprise-grade configuration management for AI services
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import { AIApiConfig, AIModelType, SupportedLanguage, ContentType, AudienceType } from '../types/aiTypes';

// ================== ENVIRONMENT CONFIGURATION ==================

export const AI_CONFIG: AIApiConfig = {
  baseURL: process.env.REACT_APP_AI_API_BASE_URL || 'http://localhost:8081/api/ai/advanced',
  timeout: parseInt(process.env.REACT_APP_AI_TIMEOUT || '30000'),
  retryAttempts: parseInt(process.env.REACT_APP_AI_RETRY_ATTEMPTS || '3'),
  apiKey: process.env.REACT_APP_AI_API_KEY,
  endpoints: {
    chatContextual: '/chat/contextual',
    chatSimple: '/chat/simple',
    clearChat: '/chat',
    activeConversations: '/chat/active',
    structuredContent: '/content/structured',
    quickStructuredContent: '/content/structured/quick',
    summarize: '/content/summarize',
    factCheck: '/content/fact-check',
    generateHeadlines: '/headlines/generate',
    generateArticle: '/article/generate',
    health: '/health',
    // TTS endpoints (base URL: /api/ai/tts)
    ttsGenerate: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/generate`,
    ttsGenerateNews: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/generate/news`,
    ttsGenerateArticle: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/generate/article`,
    ttsVoices: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/voices`,
    ttsLanguages: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/languages`,
    ttsVoiceRecommend: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/voice/recommend`,
    ttsHealth: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/ai/tts/health`
  }
};

// ================== FEATURE FLAGS ==================

export const AI_FEATURES = {
  CHATBOT_ENABLED: process.env.REACT_APP_AI_CHATBOT_ENABLED !== 'false',
  STRUCTURED_CONTENT_ENABLED: process.env.REACT_APP_AI_STRUCTURED_CONTENT_ENABLED !== 'false',
  CONTENT_ANALYSIS_ENABLED: process.env.REACT_APP_AI_CONTENT_ANALYSIS_ENABLED !== 'false',
  AUTO_SUGGEST_ENABLED: process.env.REACT_APP_AI_AUTO_SUGGEST_ENABLED !== 'false',
  REAL_TIME_ANALYSIS: process.env.REACT_APP_AI_REAL_TIME_ANALYSIS === 'true',
  OFFLINE_MODE: process.env.REACT_APP_AI_OFFLINE_MODE === 'true',
  // TTS Feature Flags
  TTS_ENABLED: process.env.REACT_APP_AI_TTS_ENABLED !== 'false',
  TTS_AUTO_GENERATE: process.env.REACT_APP_AI_TTS_AUTO_GENERATE === 'true'
};

// ================== MODEL CONFIGURATION ==================

export const AI_MODELS = {
  DEFAULT: (process.env.REACT_APP_AI_DEFAULT_MODEL as AIModelType) || AIModelType.GPT_4,
  FALLBACK: (process.env.REACT_APP_AI_FALLBACK_MODEL as AIModelType) || AIModelType.CLAUDE,
  AVAILABLE: [
    AIModelType.GPT_4,
    AIModelType.CLAUDE,
    AIModelType.GEMINI
  ],
  CAPABILITIES: {
    [AIModelType.GPT_4]: {
      maxTokens: 128000,
      supportedLanguages: ['en', 'hi', 'bn', 'te', 'mr', 'ta', 'gu', 'kn', 'or', 'pa', 'ml', 'as', 'ur'],
      features: ['chat', 'generation', 'analysis'],
      costPerToken: 0.00003
    },
    [AIModelType.CLAUDE]: {
      maxTokens: 200000,
      supportedLanguages: ['en', 'hi', 'es', 'fr', 'de'],
      features: ['chat', 'generation', 'analysis'],
      costPerToken: 0.000015
    },
    [AIModelType.GEMINI]: {
      maxTokens: 1000000,
      supportedLanguages: ['en', 'hi', 'bn', 'te', 'mr', 'ta'],
      features: ['chat', 'generation', 'multimodal'],
      costPerToken: 0.0000075
    }
  }
};

// ================== LANGUAGE CONFIGURATION ==================

export const LANGUAGE_CONFIG: Record<SupportedLanguage, {
  code: string;
  name: string;
  nativeName: string;
  rtl: boolean;
  enabled: boolean;
  modelSupport: AIModelType[];
}> = {
  'English': {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.CLAUDE, AIModelType.GEMINI]
  },
  'Hindi': {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिंदी',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.CLAUDE, AIModelType.GEMINI]
  },
  'Bengali': {
    code: 'bn',
    name: 'Bengali',
    nativeName: 'বাংলা',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.GEMINI]
  },
  'Telugu': {
    code: 'te',
    name: 'Telugu',
    nativeName: 'తెలుగు',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.GEMINI]
  },
  'Marathi': {
    code: 'mr',
    name: 'Marathi',
    nativeName: 'मराठी',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.GEMINI]
  },
  'Tamil': {
    code: 'ta',
    name: 'Tamil',
    nativeName: 'தமிழ்',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.GEMINI]
  },
  'Gujarati': {
    code: 'gu',
    name: 'Gujarati',
    nativeName: 'ગુજરાતી',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Kannada': {
    code: 'kn',
    name: 'Kannada',
    nativeName: 'ಕನ್ನಡ',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Odia': {
    code: 'or',
    name: 'Odia',
    nativeName: 'ଓଡ଼ିଆ',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Punjabi': {
    code: 'pa',
    name: 'Punjabi',
    nativeName: 'ਪੰਜਾਬੀ',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Malayalam': {
    code: 'ml',
    name: 'Malayalam',
    nativeName: 'മലയാളം',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Assamese': {
    code: 'as',
    name: 'Assamese',
    nativeName: 'অসমীয়া',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Urdu': {
    code: 'ur',
    name: 'Urdu',
    nativeName: 'اردو',
    rtl: true,
    enabled: true,
    modelSupport: [AIModelType.GPT_4]
  },
  'Spanish': {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.CLAUDE]
  },
  'French': {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.CLAUDE]
  },
  'German': {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    rtl: false,
    enabled: true,
    modelSupport: [AIModelType.GPT_4, AIModelType.CLAUDE]
  },
  'Chinese': {
    code: 'zh',
    name: 'Chinese',
    nativeName: '中文',
    rtl: false,
    enabled: false, // Disabled for initial release
    modelSupport: [AIModelType.GPT_4]
  },
  'Arabic': {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    rtl: true,
    enabled: false, // Disabled for initial release
    modelSupport: [AIModelType.GPT_4]
  }
};

// ================== CONTENT TYPE CONFIGURATION ==================

export const CONTENT_TYPE_CONFIG: Record<ContentType, {
  displayName: string;
  description: string;
  icon?: string;
  defaultLength: 'short' | 'medium' | 'long';
  recommendedModel: AIModelType;
  template?: string;
}> = {
  'news article': {
    displayName: 'News Article',
    description: 'Breaking news and current events coverage',
    icon: '📰',
    defaultLength: 'medium',
    recommendedModel: AIModelType.GPT_4,
    template: 'inverted pyramid structure with lead, body, and conclusion'
  },
  'blog post': {
    displayName: 'Blog Post',
    description: 'Informal, conversational content for blogs',
    icon: '📝',
    defaultLength: 'long',
    recommendedModel: AIModelType.CLAUDE,
    template: 'engaging introduction, detailed body, and call-to-action'
  },
  'social media post': {
    displayName: 'Social Media Post',
    description: 'Short, engaging content for social platforms',
    icon: '📱',
    defaultLength: 'short',
    recommendedModel: AIModelType.GEMINI,
    template: 'hook, value, and engagement prompt'
  },
  'press release': {
    displayName: 'Press Release',
    description: 'Official announcements for media distribution',
    icon: '📄',
    defaultLength: 'medium',
    recommendedModel: AIModelType.GPT_4,
    template: 'headline, dateline, lead paragraph, body, and boilerplate'
  },
  'informative article': {
    displayName: 'Informative Article',
    description: 'Educational and explanatory content',
    icon: '📚',
    defaultLength: 'long',
    recommendedModel: AIModelType.CLAUDE,
    template: 'clear structure with headings, explanations, and examples'
  },
  'opinion piece': {
    displayName: 'Opinion Piece',
    description: 'Editorial content expressing viewpoints',
    icon: '💭',
    defaultLength: 'medium',
    recommendedModel: AIModelType.GPT_4,
    template: 'thesis, arguments, counterarguments, and conclusion'
  },
  'feature story': {
    displayName: 'Feature Story',
    description: 'In-depth narrative journalism',
    icon: '🎯',
    defaultLength: 'long',
    recommendedModel: AIModelType.CLAUDE,
    template: 'narrative arc with character development and detailed reporting'
  },
  'technical documentation': {
    displayName: 'Technical Documentation',
    description: 'Detailed technical guides and documentation',
    icon: '⚙️',
    defaultLength: 'long',
    recommendedModel: AIModelType.GPT_4,
    template: 'overview, prerequisites, step-by-step instructions, and troubleshooting'
  },
  'marketing copy': {
    displayName: 'Marketing Copy',
    description: 'Persuasive content for marketing purposes',
    icon: '🎯',
    defaultLength: 'medium',
    recommendedModel: AIModelType.GEMINI,
    template: 'attention, interest, desire, and action (AIDA) framework'
  }
};

// ================== AUDIENCE CONFIGURATION ==================

export const AUDIENCE_CONFIG: Record<AudienceType, {
  displayName: string;
  description: string;
  toneRecommendations: string[];
  complexityLevel: 'basic' | 'intermediate' | 'advanced';
  recommendedModel: AIModelType;
}> = {
  'general readers': {
    displayName: 'General Readers',
    description: 'Broad audience with varied backgrounds',
    toneRecommendations: ['conversational', 'clear', 'accessible'],
    complexityLevel: 'basic',
    recommendedModel: AIModelType.GPT_4
  },
  'professionals': {
    displayName: 'Professionals',
    description: 'Working professionals in various industries',
    toneRecommendations: ['professional', 'informative', 'concise'],
    complexityLevel: 'intermediate',
    recommendedModel: AIModelType.CLAUDE
  },
  'students': {
    displayName: 'Students',
    description: 'Educational content for learners',
    toneRecommendations: ['educational', 'encouraging', 'structured'],
    complexityLevel: 'intermediate',
    recommendedModel: AIModelType.GPT_4
  },
  'experts': {
    displayName: 'Industry Experts',
    description: 'Subject matter experts and specialists',
    toneRecommendations: ['technical', 'precise', 'detailed'],
    complexityLevel: 'advanced',
    recommendedModel: AIModelType.CLAUDE
  },
  'children': {
    displayName: 'Children',
    description: 'Young readers and learners',
    toneRecommendations: ['simple', 'fun', 'engaging'],
    complexityLevel: 'basic',
    recommendedModel: AIModelType.GEMINI
  },
  'seniors': {
    displayName: 'Senior Citizens',
    description: 'Older adults with life experience',
    toneRecommendations: ['respectful', 'clear', 'patient'],
    complexityLevel: 'basic',
    recommendedModel: AIModelType.GPT_4
  },
  'technical audience': {
    displayName: 'Technical Audience',
    description: 'Developers, engineers, and technical professionals',
    toneRecommendations: ['precise', 'detailed', 'analytical'],
    complexityLevel: 'advanced',
    recommendedModel: AIModelType.CLAUDE
  },
  'business audience': {
    displayName: 'Business Audience',
    description: 'Business leaders and decision makers',
    toneRecommendations: ['strategic', 'results-focused', 'professional'],
    complexityLevel: 'intermediate',
    recommendedModel: AIModelType.GPT_4
  }
};

// ================== PERFORMANCE CONFIGURATION ==================

export const PERFORMANCE_CONFIG = {
  CACHE: {
    CONVERSATIONS_TTL: 1000 * 60 * 60 * 24, // 24 hours
    STRUCTURED_CONTENT_TTL: 1000 * 60 * 60 * 2, // 2 hours
    ANALYSIS_RESULTS_TTL: 1000 * 60 * 30, // 30 minutes
    MAX_CACHE_SIZE: 100 // Maximum number of cached items
  },
  RATE_LIMITING: {
    REQUESTS_PER_MINUTE: 60,
    REQUESTS_PER_HOUR: 1000,
    REQUESTS_PER_DAY: 10000
  },
  TIMEOUTS: {
    CHAT_MESSAGE: 15000, // 15 seconds
    CONTENT_GENERATION: 45000, // 45 seconds
    CONTENT_ANALYSIS: 30000, // 30 seconds
    HEALTH_CHECK: 5000, // 5 seconds
    TTS_GENERATION: 60000 // 60 seconds for audio generation
  },
  RETRY: {
    MAX_ATTEMPTS: 3,
    BACKOFF_MULTIPLIER: 2,
    INITIAL_DELAY: 1000,
    MAX_DELAY: 10000
  }
};

// ================== SECURITY CONFIGURATION ==================

export const SECURITY_CONFIG = {
  CONTENT_FILTERING: {
    ENABLED: true,
    PROFANITY_FILTER: true,
    SENSITIVE_CONTENT_DETECTION: true,
    SPAM_DETECTION: true
  },
  PRIVACY: {
    ANONYMIZE_CONVERSATIONS: true,
    DATA_RETENTION_DAYS: 30,
    ENCRYPT_STORED_DATA: true,
    LOG_USER_ACTIONS: false
  },
  API_SECURITY: {
    USE_HTTPS: true,
    VALIDATE_CERTIFICATES: true,
    API_KEY_ROTATION_DAYS: 90,
    REQUEST_SIGNING: false
  }
};

// ================== UTILITY FUNCTIONS ==================

export const getEnabledLanguages = (): SupportedLanguage[] => {
  return Object.entries(LANGUAGE_CONFIG)
    .filter(([_, config]) => config.enabled)
    .map(([language]) => language as SupportedLanguage);
};

export const getLanguagesByModel = (model: AIModelType): SupportedLanguage[] => {
  return Object.entries(LANGUAGE_CONFIG)
    .filter(([_, config]) => config.enabled && config.modelSupport.includes(model))
    .map(([language]) => language as SupportedLanguage);
};

export const getBestModelForLanguage = (language: SupportedLanguage): AIModelType => {
  const config = LANGUAGE_CONFIG[language];
  return config?.modelSupport[0] || AI_MODELS.DEFAULT;
};

export const validateConfiguration = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check required environment variables
  if (!AI_CONFIG.baseURL) {
    errors.push('AI_CONFIG.baseURL is required');
  }

  // Validate model configuration
  if (!Object.values(AIModelType).includes(AI_MODELS.DEFAULT)) {
    errors.push('Invalid default AI model configured');
  }

  // Check enabled languages have model support
  const enabledLanguages = getEnabledLanguages();
  enabledLanguages.forEach(language => {
    const config = LANGUAGE_CONFIG[language];
    if (config.modelSupport.length === 0) {
      errors.push(`Language ${language} has no model support configured`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// ================== EXPORT CONFIGURATION ==================

export default {
  AI_CONFIG,
  AI_FEATURES,
  AI_MODELS,
  LANGUAGE_CONFIG,
  CONTENT_TYPE_CONFIG,
  AUDIENCE_CONFIG,
  PERFORMANCE_CONFIG,
  SECURITY_CONFIG,
  // Utility functions
  getEnabledLanguages,
  getLanguagesByModel,
  getBestModelForLanguage,
  validateConfiguration
};
